"use client";

import { useEffect, useMemo, useState } from "react";
import { AdminCatalogImport } from "../admin-catalog-import";
import { DriveAdminPanel } from "../drive-admin-panel";
import { AdminChallenges } from "../admin-challenges";

type AdminSection = "Vue d’ensemble" | "Pharmacies" | "Opérateurs" | "Challenges" | "Modules" | "Drive" | "Catalogues LGPI" | "Alerte comptoir" | "Compagnon Windows" | "Réglages";

type AlertTheme = "sunny" | "mint" | "coral" | "blue";

type CompanionAlertSettings = {
  endSaleEnabled: boolean;
  title: string;
  message: string;
  buttonLabel: string;
  theme: AlertTheme;
  showConfetti: boolean;
  stayVisible: boolean;
  updatedAt?: number;
};

type EndSalePopup = {
  id: number;
  title: string;
  message: string;
  buttonLabel: string;
  pharmacies: string[];
  enabled: boolean;
  createdAt?: number;
  updatedAt?: number;
};

const newEndSalePopup = (): EndSalePopup => ({
  id: 0,
  title: "",
  message: "",
  buttonLabel: "✓ Prise en compte",
  pharmacies: ["Square", "Ragadan", "Laurentine"],
  enabled: true,
});

type CounterAlertRecurrence = "none" | "daily" | "weekdays" | "weekly";
type CounterAlertTiming = "immediate" | "scheduled" | "recurring";

type CounterAlert = {
  id: number;
  title: string;
  message: string;
  pharmacies: string[];
  scheduledAt: number;
  recurrence: CounterAlertRecurrence;
  channels: { windows: boolean; portal: boolean; tv: boolean };
  status: "active" | "inactive";
  createdBy: string;
  occurrenceAt?: number;
  occurrenceKey?: string;
};

const localDateTimeValue = (timestamp = Date.now() + 5 * 60_000) => {
  const date = new Date(timestamp);
  const offset = date.getTimezoneOffset() * 60_000;
  return new Date(date.getTime() - offset).toISOString().slice(0, 16);
};

type CompanionWorkstation = {
  workstation_id: string;
  workstation_name: string;
  pharmacy: string;
  agent_role?: "sale" | "order";
  version: string;
  trigger_count: number;
  auto_start: number;
  last_pull_at: number;
  last_seen_at: number;
  last_scan_gtin?: string;
  last_scan_kind?: string;
  last_scan_alert_count?: number;
  last_scan_raw_length?: number;
  last_scan_at?: number;
};

type AdminRole = "Pharmacien Titulaire" | "Pharmacien" | "Préparateur(trice)" | "Rayonniste";
type AdminOperator = { id?: number; name: string; pharmacy: string; role: AdminRole; birthDate: string };
const adminRoles: AdminRole[] = ["Pharmacien Titulaire", "Pharmacien", "Préparateur(trice)", "Rayonniste"];
const operatorIcon = (role: AdminRole) => role === "Pharmacien Titulaire" ? "🧑‍⚕️" : role === "Pharmacien" ? "💊" : role === "Rayonniste" ? "📦" : "⚗️";

const defaultAdminOperators: AdminOperator[] = [
  { name: "Vincent B", pharmacy: "Square", role: "Pharmacien Titulaire", birthDate: "" },
  { name: "Florian B", pharmacy: "Square", role: "Pharmacien Titulaire", birthDate: "" },
  { name: "Céline B", pharmacy: "Square", role: "Préparateur(trice)", birthDate: "" },
  { name: "Cécile F", pharmacy: "Square", role: "Rayonniste", birthDate: "" },
  { name: "Elise G", pharmacy: "Square", role: "Pharmacien", birthDate: "" },
  { name: "Fiona", pharmacy: "Square", role: "Préparateur(trice)", birthDate: "" },
  { name: "Sylvain P", pharmacy: "Square", role: "Préparateur(trice)", birthDate: "" },
  { name: "Yasmine", pharmacy: "Square", role: "Préparateur(trice)", birthDate: "" },
  { name: "Florian C", pharmacy: "Ragadan", role: "Pharmacien", birthDate: "1996-01-31" },
  { name: "Gaëlle", pharmacy: "Ragadan", role: "Préparateur(trice)", birthDate: "1998-10-20" },
  { name: "Julie", pharmacy: "Ragadan", role: "Préparateur(trice)", birthDate: "" },
  { name: "Karen", pharmacy: "Ragadan", role: "Préparateur(trice)", birthDate: "1972-03-27" },
  { name: "Karine", pharmacy: "Ragadan", role: "Préparateur(trice)", birthDate: "1977-06-23" },
  { name: "Anissa", pharmacy: "Laurentine", role: "Préparateur(trice)", birthDate: "1997-05-11" },
  { name: "Magali", pharmacy: "Laurentine", role: "Préparateur(trice)", birthDate: "1990-01-24" },
  { name: "MC", pharmacy: "Laurentine", role: "Préparateur(trice)", birthDate: "1994-05-06" },
  { name: "Sandrine", pharmacy: "Laurentine", role: "Préparateur(trice)", birthDate: "1979-12-26" },
  { name: "Tracy", pharmacy: "Laurentine", role: "Préparateur(trice)", birthDate: "1994-02-07" },
  { name: "Val", pharmacy: "Laurentine", role: "Préparateur(trice)", birthDate: "1992-10-05" },
];

const adminPharmacies = [
  { name: "Square", address: "613 av. de la Libération", color: "green", team: 8, email: "phiedusquare@gmail.com" },
  { name: "Ragadan", address: "324 bd de Provence", color: "blue", team: 5, email: "phieduragadan@gmail.com" },
  { name: "Laurentine", address: "650 av. du Général de Gaulle", color: "orange", team: 6, email: "pharmacielaurentine@gmail.com" },
];

const initialModules = [
  { name: "Patients à suivre", icon: "📞", area: "Patients", enabled: true },
  { name: "Missions récurrentes", icon: "🔁", area: "Équipe", enabled: true },
  { name: "Challenge équipe", icon: "🏁", area: "Équipe", enabled: true },
  { name: "Agenda & suivi labo", icon: "🤝", area: "Équipe", enabled: true },
  { name: "To-do & livraisons", icon: "⚡", area: "Opérations", enabled: true },
  { name: "Courses", icon: "🛒", area: "Opérations", enabled: true },
  { name: "Bloc-notes opérateur", icon: "📌", area: "Équipe", enabled: true },
  { name: "Suivi adrénaline", icon: "💉", area: "Sécurité", enabled: true },
  { name: "Indus CPAM", icon: "💶", area: "Administratif", enabled: true },
  { name: "Produits manquants à contrôler au quotidien", icon: "🔎", area: "Stock", enabled: true },
  { name: "Génériques manquants", icon: "💊", area: "Stock", enabled: true },
  { name: "Stock & étiquettes", icon: "🏷️", area: "Stock", enabled: true },
  { name: "Écran équipe TV", icon: "📺", area: "Équipe", enabled: true },
  { name: "Nettoyer une ordonnance", icon: "📸", area: "Comptoir", enabled: true },
  { name: "Mémo conseil patient", icon: "🗒️", area: "Comptoir", enabled: true },
];

export default function AdminPage() {
  const [section, setSection] = useState<AdminSection>("Vue d’ensemble");
  const [modules, setModules] = useState(initialModules);
  const [operators, setOperators] = useState<AdminOperator[]>(defaultAdminOperators);
  const [operatorQuery, setOperatorQuery] = useState("");
  const [operatorPharmacyFilter, setOperatorPharmacyFilter] = useState("Toutes");
  const [editingOperator, setEditingOperator] = useState<AdminOperator | null>(null);
  const [operatorSaving, setOperatorSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [stats, setStats] = useState({ missions: 0, tasks: 0, patients: 0, alerts: 0, shopping: 0 });
  const [settings, setSettings] = useState({ tvAlerts: true, overdueAlerts: true, patientNamesOnTv: true, dailyDigest: false });
  const [portalOperator, setPortalOperator] = useState("Vincent B");
  const [aiStatus, setAiStatus] = useState<{ configured: boolean; model: string } | null>(null);
  const [companionStatus, setCompanionStatus] = useState<{
    workstations: CompanionWorkstation[];
    currentVersion: string;
    onlineThresholdMs: number;
    generatedAt: number;
    niftyState: { sourceUrl: string; eanCount: number; lastRefreshAt: number; lastAttemptAt: number; lastError: string };
  }>({ workstations: [], currentVersion: "1.7.13", onlineThresholdMs: 10 * 60 * 1000, generatedAt: 0, niftyState: { sourceUrl: "", eanCount: 0, lastRefreshAt: 0, lastAttemptAt: 0, lastError: "" } });
  const [companionLoading, setCompanionLoading] = useState(false);
  const [alertSettings, setAlertSettings] = useState<CompanionAlertSettings>({
    endSaleEnabled: true,
    title: "Un conseil pour ce patient ✨",
    message: "Une information utile est disponible pour accompagner la délivrance.",
    buttonLabel: "✓ Prise en compte",
    theme: "sunny",
    showConfetti: true,
    stayVisible: true,
  });
  const [alertSettingsLoading, setAlertSettingsLoading] = useState(false);
  const [endSalePopups, setEndSalePopups] = useState<EndSalePopup[]>([]);
  const [endSaleDraft, setEndSaleDraft] = useState<EndSalePopup>(newEndSalePopup());
  const [endSalePopupLoading, setEndSalePopupLoading] = useState(false);
  const [counterAlerts, setCounterAlerts] = useState<CounterAlert[]>([]);
  const [counterAlertLoading, setCounterAlertLoading] = useState(false);
  const [backupBusy, setBackupBusy] = useState(false);
  const [backupFile, setBackupFile] = useState<File | null>(null);
  const [backupPreview, setBackupPreview] = useState<{ exportedAt: string; tables: number; rows: number } | null>(null);
  const [counterAlertDraft, setCounterAlertDraft] = useState({
    title: "",
    message: "",
    pharmacies: [] as string[],
    scheduledAt: localDateTimeValue(),
    timing: "immediate" as CounterAlertTiming,
    recurrence: "none" as CounterAlertRecurrence,
    channels: { windows: true, portal: false, tv: false },
  });

  useEffect(() => {
    const savedOperator = window.localStorage.getItem("portal-active-operator");
    if (savedOperator && defaultAdminOperators.some((operator) => operator.name === savedOperator)) {
      setPortalOperator(savedOperator);
    }
    void fetch("/api/operators", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload: { operators?: AdminOperator[] } | null) => { if (payload?.operators?.length) setOperators(payload.operators); })
      .catch(() => undefined);
    void fetch("/api/prescription-ai", { cache: "no-store" })
      .then((response) => response.ok ? response.json() : null)
      .then((payload) => {
        if (payload) setAiStatus(payload);
      })
      .catch(() => undefined);

    const count = (payload: unknown, key: string) => {
      if (!payload || typeof payload !== "object") return 0;
      const value = (payload as Record<string, unknown>)[key];
      return Array.isArray(value) ? value.length : 0;
    };
    const loadStats = async () => {
      const endpoints = [
        ["/api/recurring-missions", "missions"],
        ["/api/daily-tasks", "tasks"],
        ["/api/patient-followups", "followups"],
        ["/api/adrenaline", "items"],
        ["/api/shopping-list", "items"],
      ] as const;
      const results = await Promise.all(endpoints.map(async ([url, key]) => {
        try {
          const response = await fetch(url, { cache: "no-store" });
          return response.ok ? count(await response.json(), key) : 0;
        } catch {
          return 0;
        }
      }));
      setStats({ missions: results[0], tasks: results[1], patients: results[2], alerts: results[3], shopping: results[4] });
    };
    void loadStats();
  }, []);

  const loadCompanionStatus = async () => {
    setCompanionLoading(true);
    try {
      const response = await fetch("/api/windows-companion?mode=status", { cache: "no-store" });
      if (!response.ok) throw new Error("status");
      const payload = await response.json() as Partial<typeof companionStatus>;
      setCompanionStatus((current) => ({
        ...current,
        ...payload,
        workstations: payload.workstations ?? [],
        onlineThresholdMs: payload.onlineThresholdMs ?? current.onlineThresholdMs,
        generatedAt: payload.generatedAt ?? Date.now(),
        niftyState: payload.niftyState ?? current.niftyState,
      }));
    } catch {
      toast("Impossible d’actualiser les postes pour le moment");
    } finally {
      setCompanionLoading(false);
    }
  };

  const refreshNiftyNow = async () => {
    setCompanionLoading(true);
    try {
      const response = await fetch("/api/windows-companion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "refresh_nifty" }),
      });
      const payload = await response.json() as { lastError?: string };
      if (!response.ok || payload.lastError) throw new Error(payload.lastError || "Actualisation impossible");
      toast("Offres Nifty actualisées");
      await loadCompanionStatus();
    } catch (error) {
      toast(error instanceof Error ? error.message : "Actualisation Nifty impossible");
      await loadCompanionStatus();
    } finally {
      setCompanionLoading(false);
    }
  };

  const loadAlertSettings = async () => {
    setAlertSettingsLoading(true);
    try {
      const response = await fetch("/api/windows-companion?mode=alert-settings", { cache: "no-store" });
      if (!response.ok) throw new Error("settings");
      setAlertSettings(await response.json() as CompanionAlertSettings);
    } catch {
      toast("Impossible de charger le modèle d’alerte pour le moment");
    } finally {
      setAlertSettingsLoading(false);
    }
  };

  const loadEndSalePopups = async () => {
    setEndSalePopupLoading(true);
    try {
      const response = await fetch("/api/windows-companion?mode=end-sale-popups", { cache: "no-store" });
      const payload = await response.json() as { items?: EndSalePopup[]; error?: string };
      if (!response.ok) throw new Error(payload.error || "Chargement impossible");
      setEndSalePopups(payload.items ?? []);
    } catch (error) {
      toast(error instanceof Error ? error.message : "Impossible de charger les pop-ups de fin de vente");
    } finally {
      setEndSalePopupLoading(false);
    }
  };

  const sendEndSalePopup = async (popup: EndSalePopup) => {
    const response = await fetch("/api/windows-companion", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action: "save_end_sale_popup", ...popup }),
    });
    const payload = await response.json() as EndSalePopup & { error?: string };
    if (!response.ok) throw new Error(payload.error || "Enregistrement impossible");
    return payload;
  };

  const saveEndSalePopup = async () => {
    if (!endSaleDraft.title.trim()) return toast("Renseignez le titre du pop-up");
    if (!endSaleDraft.pharmacies.length) return toast("Choisissez au moins une pharmacie");
    setEndSalePopupLoading(true);
    try {
      const saved = await sendEndSalePopup(endSaleDraft);
      setEndSalePopups((current) => [saved, ...current.filter((item) => item.id !== saved.id)]);
      setEndSaleDraft(newEndSalePopup());
      toast(endSaleDraft.id ? "Pop-up modifié" : "Pop-up créé et diffusé");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Le pop-up n’a pas pu être enregistré");
    } finally {
      setEndSalePopupLoading(false);
    }
  };

  const toggleEndSalePopup = async (popup: EndSalePopup) => {
    setEndSalePopupLoading(true);
    try {
      const saved = await sendEndSalePopup({ ...popup, enabled: !popup.enabled });
      setEndSalePopups((current) => current.map((item) => item.id === saved.id ? saved : item));
      if (endSaleDraft.id === saved.id) setEndSaleDraft(saved);
      toast(saved.enabled ? "Pop-up activé" : "Pop-up désactivé");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Le statut n’a pas pu être modifié");
    } finally {
      setEndSalePopupLoading(false);
    }
  };

  const deleteEndSalePopup = async (popup: EndSalePopup) => {
    if (!window.confirm(`Supprimer définitivement « ${popup.title} » ?`)) return;
    setEndSalePopupLoading(true);
    try {
      const response = await fetch(`/api/windows-companion?endSalePopupId=${popup.id}`, { method: "DELETE" });
      const payload = await response.json() as { error?: string };
      if (!response.ok) throw new Error(payload.error || "Suppression impossible");
      setEndSalePopups((current) => current.filter((item) => item.id !== popup.id));
      if (endSaleDraft.id === popup.id) setEndSaleDraft(newEndSalePopup());
      toast("Pop-up supprimé");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Le pop-up n’a pas pu être supprimé");
    } finally {
      setEndSalePopupLoading(false);
    }
  };

  const loadCounterAlerts = async () => {
    try {
      const response = await fetch("/api/counter-alerts", { cache: "no-store" });
      if (!response.ok) throw new Error("alerts");
      const payload = await response.json() as { items?: CounterAlert[] };
      setCounterAlerts(payload.items ?? []);
    } catch {
      toast("Impossible de charger les alertes programmées");
    }
  };

  const saveCounterAlert = async () => {
    setCounterAlertLoading(true);
    try {
      const response = await fetch("/api/counter-alerts", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...counterAlertDraft,
          scheduledAt: counterAlertDraft.timing === "immediate" ? undefined : new Date(counterAlertDraft.scheduledAt).getTime(),
          createdBy: portalOperator,
        }),
      });
      const payload = await response.json() as CounterAlert & { error?: string };
      if (!response.ok) throw new Error(payload.error || "Alerte invalide");
      setCounterAlerts((current) => [payload, ...current.filter((item) => item.id !== payload.id)]);
      const operatorPharmacy = operators.find((operator) => operator.name === portalOperator)?.pharmacy ?? "Square";
      setCounterAlertDraft({
        title: "",
        message: "",
        pharmacies: [operatorPharmacy],
        scheduledAt: localDateTimeValue(),
        timing: "immediate",
        recurrence: "none",
        channels: { windows: true, portal: false, tv: false },
      });
      toast(counterAlertDraft.timing === "immediate" ? "Alerte envoyée" : "Alerte programmée");
    } catch (error) {
      toast(error instanceof Error ? error.message : "L’alerte n’a pas pu être enregistrée");
    } finally {
      setCounterAlertLoading(false);
    }
  };

  const deleteCounterAlert = async (item: CounterAlert) => {
    const response = await fetch(`/api/counter-alerts?id=${item.id}`, { method: "DELETE" });
    if (response.ok) {
      setCounterAlerts((current) => current.filter((entry) => entry.id !== item.id));
      toast("Alerte supprimée");
    } else {
      toast("L’alerte n’a pas pu être supprimée");
    }
  };

  const saveAlertSettings = async () => {
    setAlertSettingsLoading(true);
    try {
      const response = await fetch("/api/windows-companion", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_alert_settings", ...alertSettings }),
      });
      if (!response.ok) throw new Error("settings");
      setAlertSettings(await response.json() as CompanionAlertSettings);
      toast("Modèle d’alerte diffusé au compagnon Windows");
    } catch {
      toast("Le modèle d’alerte n’a pas pu être enregistré");
    } finally {
      setAlertSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (section === "Compagnon Windows") void loadCompanionStatus();
    if (section === "Alerte comptoir") {
      const operatorPharmacy = operators.find((operator) => operator.name === portalOperator)?.pharmacy ?? "Square";
      setCounterAlertDraft((current) => current.pharmacies.length ? current : { ...current, pharmacies: [operatorPharmacy] });
      void loadAlertSettings();
      void loadEndSalePopups();
      void loadCounterAlerts();
    }
  }, [section]);

  const filteredOperators = useMemo(() => {
    const query = operatorQuery.trim().toLocaleLowerCase("fr");
    return operators.filter((operator) => (operatorPharmacyFilter === "Toutes" || operator.pharmacy === operatorPharmacyFilter) && (!query || `${operator.name} ${operator.pharmacy} ${operator.role}`.toLocaleLowerCase("fr").includes(query)));
  }, [operatorPharmacyFilter, operatorQuery, operators]);

  const toast = (text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice(""), 2400);
  };

  const exportPortalBackup = async () => {
    setBackupBusy(true);
    try {
      const response = await fetch("/api/admin-backup", { cache: "no-store" });
      if (!response.ok) throw new Error((await response.json() as { error?: string }).error || "Export impossible");
      const blob = await response.blob();
      const disposition = response.headers.get("Content-Disposition") ?? "";
      const fileName = disposition.match(/filename="([^"]+)"/)?.[1] || `portail-pharma-${new Date().toISOString().slice(0, 10)}.json`;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      anchor.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 2000);
      toast("Sauvegarde complète téléchargée");
    } catch (error) {
      toast(error instanceof Error ? error.message : "La sauvegarde n’a pas pu être générée");
    } finally {
      setBackupBusy(false);
    }
  };

  const inspectBackupFile = async (file: File | null) => {
    setBackupFile(null);
    setBackupPreview(null);
    if (!file) return;
    try {
      const payload = JSON.parse(await file.text()) as { format?: string; exportedAt?: string; tables?: Array<{ rows?: unknown[] }> };
      if (payload.format !== "portail-pharma-backup" || !Array.isArray(payload.tables)) throw new Error("Format inconnu");
      setBackupFile(file);
      setBackupPreview({
        exportedAt: String(payload.exportedAt || ""),
        tables: payload.tables.length,
        rows: payload.tables.reduce((sum, table) => sum + (Array.isArray(table.rows) ? table.rows.length : 0), 0),
      });
    } catch {
      toast("Ce fichier n’est pas une sauvegarde Portail Pharma valide");
    }
  };

  const restorePortalBackup = async () => {
    if (!backupFile || !backupPreview) return;
    const date = backupPreview.exportedAt ? new Date(backupPreview.exportedAt).toLocaleString("fr-FR") : "date inconnue";
    if (!window.confirm(`Réimporter la sauvegarde du ${date} ?\n\nLes données portant les mêmes identifiants seront restaurées. Les données plus récentes sans équivalent ne seront pas supprimées.`)) return;
    setBackupBusy(true);
    try {
      const response = await fetch("/api/admin-backup", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: await backupFile.text(),
      });
      const payload = await response.json() as { restoredRows?: number; restoredTables?: number; error?: string };
      if (!response.ok) throw new Error(payload.error || "Restauration impossible");
      toast(`${payload.restoredRows ?? 0} données restaurées dans ${payload.restoredTables ?? 0} rubriques`);
      setBackupFile(null);
      setBackupPreview(null);
    } catch (error) {
      toast(error instanceof Error ? error.message : "La restauration a échoué");
    } finally {
      setBackupBusy(false);
    }
  };

  const saveOperator = async () => {
    if (!editingOperator?.name.trim()) return toast("Renseignez le nom de l’opérateur");
    setOperatorSaving(true);
    try {
      const response = await fetch("/api/operators", {
        method: editingOperator.id ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editingOperator),
      });
      const payload = await response.json() as AdminOperator & { error?: string };
      if (!response.ok) throw new Error(payload.error || "Enregistrement impossible");
      setOperators((current) => editingOperator.id ? current.map((item) => item.id === editingOperator.id ? payload : item) : [...current, payload]);
      setEditingOperator(null);
      toast("Profil opérateur enregistré");
    } catch (error) {
      toast(error instanceof Error ? error.message : "Le profil n’a pas pu être enregistré");
    } finally {
      setOperatorSaving(false);
    }
  };

  const renderOverview = () => (
    <>
      <section className="admin-welcome">
        <div><span>TABLEAU DE BORD GROUPE</span><h1>Bonsoir Vincent 👋</h1><p>Les trois pharmacies en un coup d’œil, sans perdre le fil du terrain.</p></div>
        <a href="/">Retour au portail <b>→</b></a>
      </section>
      <div className="admin-stats">
        <article className="orange"><span>🔁</span><div><strong>{stats.missions}</strong><p>Missions actives</p></div><b>Voir</b></article>
        <article className="blue"><span>⚡</span><div><strong>{stats.tasks}</strong><p>Actions & livraisons</p></div><b>Direct</b></article>
        <article className="pink"><span>📞</span><div><strong>{stats.patients}</strong><p>Patients suivis</p></div><b>Suivi</b></article>
        <article className="green"><span>🛒</span><div><strong>{stats.shopping}</strong><p>Courses visibles</p></div><b>Liste</b></article>
      </div>
      <div className="admin-dashboard-grid">
        <section className="admin-panel admin-network">
          <header><div><span>RÉSEAU</span><h2>État des pharmacies</h2></div><b><i /> Tout est connecté</b></header>
          <div className="admin-pharmacy-rows">{adminPharmacies.map((item) => <article key={item.name} className={item.color}><span>{item.name === "Square" ? "🏥" : item.name === "Ragadan" ? "🌿" : "✨"}</span><div><strong>Pharmacie {item.name}</strong><small>{item.address}</small></div><p><b>{item.team}</b> opérateurs</p><em>En ligne</em></article>)}</div>
        </section>
        <section className="admin-panel admin-alert-center">
          <header><div><span>À SURVEILLER</span><h2>Centre d’alertes</h2></div><strong>{stats.alerts}</strong></header>
          <article className="safe"><span>🛡️</span><div><strong>Sécurité vaccinale</strong><small>{stats.alerts ? `${stats.alerts} suivi(s) adrénaline enregistré(s)` : "Aucune alerte urgente"}</small></div></article>
          <article className="info"><span>📺</span><div><strong>Écran équipe</strong><small>Les alertes importantes sont relayées automatiquement.</small></div></article>
          <button type="button" onClick={() => setSection("Réglages")}>Régler les alertes <span>→</span></button>
        </section>
        <section className="admin-panel admin-quick">
          <header><div><span>ACTIONS RAPIDES</span><h2>Administrer</h2></div></header>
          <div><button type="button" onClick={() => { setSection("Opérateurs"); toast("Gestion des opérateurs ouverte"); }}><span>👤</span><strong>Ajouter un opérateur</strong><small>Équipe, rôle et pharmacie</small></button><button type="button" onClick={() => { setSection("Modules"); toast("Gestion des modules ouverte"); }}><span>🧩</span><strong>Gérer les modules</strong><small>Visibilité et accès</small></button><a href="/"><span>📺</span><strong>Voir l’écran équipe</strong><small>Contrôler le rendu TV</small></a></div>
        </section>
      </div>
    </>
  );

  const renderPharmacies = () => <section className="admin-content-card"><header><div><span>STRUCTURE DU RÉSEAU</span><h1>Pharmacies</h1><p>Coordonnées, équipe et état de synchronisation.</p></div><button type="button" onClick={() => toast("Ajout de pharmacie prévu dans la prochaine étape")}>＋ Ajouter</button></header><div className="admin-pharmacy-cards">{adminPharmacies.map((item) => <article key={item.name} className={item.color}><div className="admin-pharmacy-visual">{item.name === "Square" ? "🏥" : item.name === "Ragadan" ? "🌿" : "✨"}</div><h2>Pharmacie {item.name}</h2><p>{item.address}<br />06700 Saint-Laurent-du-Var</p><dl><div><dt>Équipe</dt><dd>{item.team} personnes</dd></div><div><dt>Email</dt><dd>{item.email}</dd></div><div><dt>Connexion</dt><dd><i /> Active</dd></div></dl><button type="button" onClick={() => toast(`Fiche ${item.name} prête à être complétée`)}>Modifier la fiche <span>→</span></button></article>)}</div></section>;

  const renderOperators = () => <section className="admin-content-card admin-operators-page">
    <header><div><span>MANAGEMENT</span><h1>Opérateurs</h1><p>{operators.length} personnes ventilées par pharmacie.</p></div><button type="button" className="flash" onClick={() => setEditingOperator({ name: "", pharmacy: operatorPharmacyFilter === "Toutes" ? "Square" : operatorPharmacyFilter, role: "Pharmacien", birthDate: "" })}>＋ Nouvel opérateur</button></header>
    <div className="admin-operator-toolbar"><label className="admin-search"><span>🔎</span><input value={operatorQuery} onChange={(event) => setOperatorQuery(event.target.value)} placeholder="Rechercher un nom ou un profil…" /></label><div className="admin-operator-filters">{["Toutes", ...adminPharmacies.map((item) => item.name)].map((name) => <button type="button" className={operatorPharmacyFilter === name ? "active" : ""} key={name} onClick={() => setOperatorPharmacyFilter(name)}>{name}</button>)}</div></div>
    <div className="admin-operator-pharmacy-grid">{adminPharmacies.filter((pharmacy) => operatorPharmacyFilter === "Toutes" || pharmacy.name === operatorPharmacyFilter).map((pharmacy) => {
      const pharmacyOperators = filteredOperators.filter((operator) => operator.pharmacy === pharmacy.name);
      return <section key={pharmacy.name} className={pharmacy.color}><header><div><span>{pharmacy.name === "Square" ? "🏥" : pharmacy.name === "Ragadan" ? "🌿" : "✨"}</span><div><strong>{pharmacy.name}</strong><small>{pharmacyOperators.length} opérateur{pharmacyOperators.length > 1 ? "s" : ""}</small></div></div></header><div className="admin-operator-list">{pharmacyOperators.map((operator) => <article key={operator.id ?? operator.name}><span>{operatorIcon(operator.role)}</span><div><strong>{operator.name}</strong><small>{operator.role}{operator.birthDate ? ` · né(e) le ${operator.birthDate.split("-").reverse().join("/")}` : ""}</small></div><button type="button" onClick={() => setEditingOperator({ ...operator })}>Gérer →</button></article>)}{!pharmacyOperators.length && <p>Aucun opérateur correspondant.</p>}</div></section>;
    })}</div>
    {editingOperator && <div className="admin-operator-editor"><header><div><span>PROFIL OPÉRATEUR</span><h2>{editingOperator.id ? editingOperator.name : "Nouvel opérateur"}</h2></div><button type="button" onClick={() => setEditingOperator(null)}>×</button></header><div><label><span>Nom affiché</span><input autoFocus value={editingOperator.name} onChange={(event) => setEditingOperator({ ...editingOperator, name: event.target.value })} placeholder="Prénom et initiale" /></label><label><span>Pharmacie</span><select value={editingOperator.pharmacy} onChange={(event) => setEditingOperator({ ...editingOperator, pharmacy: event.target.value })}>{adminPharmacies.map((item) => <option key={item.name}>{item.name}</option>)}</select></label><label><span>Profil</span><select value={editingOperator.role} onChange={(event) => setEditingOperator({ ...editingOperator, role: event.target.value as AdminRole })}>{adminRoles.map((role) => <option key={role}>{role}</option>)}</select></label><label><span>Date de naissance</span><input type="date" value={editingOperator.birthDate} onChange={(event) => setEditingOperator({ ...editingOperator, birthDate: event.target.value })} /></label></div><footer><button type="button" className="quiet" onClick={() => setEditingOperator(null)}>Annuler</button><button type="button" disabled={operatorSaving} onClick={() => void saveOperator()}>{operatorSaving ? "Enregistrement…" : "💾 Enregistrer le profil"}</button></footer></div>}
  </section>;

  const renderModules = () => <section className="admin-content-card"><header><div><span>PORTAIL</span><h1>Modules</h1><p>Choisissez ce qui est visible pour les équipes.</p></div><b className="admin-enabled-count">{modules.filter((module) => module.enabled).length} actifs</b></header><div className="admin-module-grid">{modules.map((module) => <article key={module.name} className={module.enabled ? "enabled" : "disabled"}><span>{module.icon}</span><div><strong>{module.name}</strong><small>{module.area}</small></div><button type="button" className="admin-switch" aria-pressed={module.enabled} onClick={() => setModules((current) => current.map((item) => item.name === module.name ? { ...item, enabled: !item.enabled } : item))}><i /></button></article>)}</div><div className="admin-save-row"><p>Les modifications sont préparées ici. La gestion fine des droits par rôle viendra ensuite.</p><button type="button" onClick={() => toast("Configuration des modules enregistrée pour cette session")}>💾 Enregistrer</button></div></section>;

  const renderAlertStudio = () => {
    const needsSchedule = counterAlertDraft.timing !== "immediate";
    const buttonLabel = counterAlertDraft.timing === "immediate"
      ? "Envoyer maintenant"
      : counterAlertDraft.timing === "scheduled" ? "Programmer l’envoi" : "Programmer la récurrence";
    return (
      <section className="admin-content-card admin-counter-center">
        <header>
          <div><span>MESSAGE ÉQUIPE</span><h1>Alerte comptoir</h1><p>Gérez le rappel de fin de vente et les messages ponctuels de l’équipe.</p></div>
          <b className="admin-alert-ready">● COMPAGNON CONNECTÉ</b>
        </header>

        <section className="admin-sale-end-settings">
          <header><span>✓</span><div><small>LGPI · ÉCRAN DE RÈGLEMENT</small><h2>Pop-ups de fin de vente</h2><p>Créez plusieurs messages, choisissez leurs pharmacies et pilotez ceux qui sont actuellement diffusés.</p></div><b>{endSalePopups.filter((item) => item.enabled).length} actif{endSalePopups.filter((item) => item.enabled).length > 1 ? "s" : ""}</b></header>
          <div className="admin-sale-end-grid">
            <div className="admin-sale-end-editor">
              <div className="admin-sale-end-editor-title"><div><small>{endSaleDraft.id ? "MODIFICATION" : "NOUVEAU POP-UP"}</small><strong>{endSaleDraft.id ? endSaleDraft.title : "Préparer un message"}</strong></div>{endSaleDraft.id > 0 && <button type="button" onClick={() => setEndSaleDraft(newEndSalePopup())}>＋ Nouveau</button>}</div>
              <label><span>Titre du pop-up</span><input value={endSaleDraft.title} maxLength={120} onChange={(event) => setEndSaleDraft({ ...endSaleDraft, title: event.target.value })} placeholder="Ex. Penser à proposer le service…" /></label>
              <label><span>Message</span><textarea value={endSaleDraft.message} maxLength={500} onChange={(event) => setEndSaleDraft({ ...endSaleDraft, message: event.target.value })} placeholder="Le message affiché après la vente" /></label>
              <label><span>Bouton de validation</span><input value={endSaleDraft.buttonLabel} maxLength={48} onChange={(event) => setEndSaleDraft({ ...endSaleDraft, buttonLabel: event.target.value })} /></label>
              <fieldset><legend>Pharmacies concernées</legend><div className="admin-sale-end-pharmacies">{adminPharmacies.map((item) => <label key={item.name} className={endSaleDraft.pharmacies.includes(item.name) ? "selected" : ""}><input type="checkbox" checked={endSaleDraft.pharmacies.includes(item.name)} onChange={() => setEndSaleDraft({ ...endSaleDraft, pharmacies: endSaleDraft.pharmacies.includes(item.name) ? endSaleDraft.pharmacies.filter((name) => name !== item.name) : [...endSaleDraft.pharmacies, item.name] })} /><span>{item.name}</span></label>)}</div></fieldset>
              <label className="admin-sale-end-active"><input type="checkbox" checked={endSaleDraft.enabled} onChange={(event) => setEndSaleDraft({ ...endSaleDraft, enabled: event.target.checked })} /><span><strong>Activer dès l’enregistrement</strong><small>Le pop-up sera transmis aux postes des pharmacies choisies.</small></span></label>
              <button type="button" className="admin-sale-end-save" disabled={endSalePopupLoading || !endSaleDraft.title.trim() || !endSaleDraft.pharmacies.length} onClick={() => void saveEndSalePopup()}>{endSalePopupLoading ? "Enregistrement…" : endSaleDraft.id ? "💾 Enregistrer les modifications" : "＋ Créer le pop-up"}</button>
            </div>
            <aside className={endSaleDraft.enabled ? "active" : "disabled"}><small>APERÇU SUR LE POSTE DE VENTE</small><strong>{endSaleDraft.title || "Votre titre"}</strong><p>{endSaleDraft.message || "Votre message apparaîtra ici."}</p><button type="button">{endSaleDraft.buttonLabel || "J’ai compris"}</button><footer>{endSaleDraft.enabled ? `Actif pour ${endSaleDraft.pharmacies.join(" · ") || "aucune pharmacie"}` : "Pop-up désactivé"}</footer></aside>
          </div>
          <div className="admin-sale-end-current"><header><div><small>DIFFUSION EN COURS</small><h3>Gérer les pop-ups</h3></div><button type="button" onClick={() => setEndSaleDraft(newEndSalePopup())}>＋ Nouveau pop-up</button></header>{endSalePopupLoading && !endSalePopups.length ? <p>Chargement…</p> : endSalePopups.length ? <div>{endSalePopups.map((popup) => <article key={popup.id} className={popup.enabled ? "enabled" : "disabled"}><span>{popup.enabled ? "✓" : "Ⅱ"}</span><div><strong>{popup.title}</strong><p>{popup.message || "Aucun message complémentaire"}</p><small>{popup.pharmacies.join(" · ")} · {popup.enabled ? "ACTIF" : "DÉSACTIVÉ"}</small></div><nav><button type="button" onClick={() => setEndSaleDraft({ ...popup })}>Modifier</button><button type="button" onClick={() => void toggleEndSalePopup(popup)}>{popup.enabled ? "Désactiver" : "Activer"}</button><button type="button" className="danger" onClick={() => void deleteEndSalePopup(popup)}>Supprimer</button></nav></article>)}</div> : <p>Aucun pop-up enregistré. Créez le premier ci-dessus.</p>}</div>
        </section>

        <div className="admin-counter-window">
          <div className="admin-counter-window-title"><span>🔔</span><div><h2>Nouvelle alerte</h2><p>Une consigne courte, au bon endroit et au bon moment.</p></div></div>
          <label><span>Titre</span><input autoFocus value={counterAlertDraft.title} maxLength={120} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, title: event.target.value })} placeholder="Ex. Appeler le grossiste" /></label>
          <label><span>Message <small>(facultatif)</small></span><textarea value={counterAlertDraft.message} maxLength={700} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, message: event.target.value })} placeholder="La consigne courte à afficher" /></label>

          <fieldset><legend>Quelle pharmacie ?</legend><div className="admin-counter-choice-row">{adminPharmacies.map((item) => <label key={item.name} className={counterAlertDraft.pharmacies.includes(item.name) ? "selected" : ""}><input type="checkbox" checked={counterAlertDraft.pharmacies.includes(item.name)} onChange={() => setCounterAlertDraft({ ...counterAlertDraft, pharmacies: counterAlertDraft.pharmacies.includes(item.name) ? counterAlertDraft.pharmacies.filter((name) => name !== item.name) : [...counterAlertDraft.pharmacies, item.name] })} /><span>{item.name}</span></label>)}</div></fieldset>

          <fieldset><legend>Quand ?</legend><div className="admin-counter-timing">{([
            ["immediate", "⚡", "Immédiatement"],
            ["scheduled", "🕘", "En différé"],
            ["recurring", "🔁", "Récurrence"],
          ] as const).map(([value, icon, label]) => <button key={value} type="button" className={counterAlertDraft.timing === value ? "selected" : ""} onClick={() => setCounterAlertDraft({ ...counterAlertDraft, timing: value, recurrence: value === "recurring" ? (counterAlertDraft.recurrence === "none" ? "daily" : counterAlertDraft.recurrence) : "none" })}><span>{icon}</span><strong>{label}</strong></button>)}</div></fieldset>

          {needsSchedule && <div className="admin-counter-schedule-grid compact">
            <label><span>Date et heure</span><input type="datetime-local" value={counterAlertDraft.scheduledAt} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, scheduledAt: event.target.value })} /></label>
            {counterAlertDraft.timing === "recurring" && <label><span>Répéter</span><select value={counterAlertDraft.recurrence} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, recurrence: event.target.value as CounterAlertRecurrence })}><option value="daily">Tous les jours</option><option value="weekdays">Du lundi au vendredi</option><option value="weekly">Chaque semaine</option></select></label>}
          </div>}

          <fieldset><legend>Afficher sur</legend><div className="admin-counter-choice-row channels">
            <label className={counterAlertDraft.channels.tv ? "selected" : ""}><input type="checkbox" checked={counterAlertDraft.channels.tv} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, channels: { ...counterAlertDraft.channels, tv: event.target.checked } })} /><span>📺 TV</span></label>
            <label className={counterAlertDraft.channels.windows ? "selected" : ""}><input type="checkbox" checked={counterAlertDraft.channels.windows} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, channels: { ...counterAlertDraft.channels, windows: event.target.checked } })} /><span>🖥️ LGPI</span></label>
            <label className={counterAlertDraft.channels.portal ? "selected" : ""}><input type="checkbox" checked={counterAlertDraft.channels.portal} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, channels: { ...counterAlertDraft.channels, portal: event.target.checked } })} /><span>🌐 Portail</span></label>
          </div></fieldset>

          <button type="button" className="admin-counter-send" disabled={counterAlertLoading || !counterAlertDraft.title.trim() || !counterAlertDraft.pharmacies.length || (needsSchedule && !counterAlertDraft.scheduledAt) || !Object.values(counterAlertDraft.channels).some(Boolean)} onClick={() => void saveCounterAlert()}>{counterAlertLoading ? "Envoi…" : `🔔 ${buttonLabel}`}</button>
        </div>

        {!!counterAlerts.length && <section className="admin-counter-history"><h2>Alertes actives</h2><div className="admin-counter-alert-list">{counterAlerts.slice(0, 8).map((item) => <article key={item.id}><span>{item.status === "active" ? "●" : "○"}</span><div><strong>{item.title}</strong><small>{item.pharmacies.join(", ")} · {new Date(item.scheduledAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {[item.channels.tv && "TV", item.channels.windows && "LGPI", item.channels.portal && "Portail"].filter(Boolean).join(" + ")}</small></div><button type="button" aria-label={`Supprimer ${item.title}`} onClick={() => void deleteCounterAlert(item)}>×</button></article>)}</div></section>}
      </section>
    );
  };

  const renderAlertStudioLegacy = () => {
    const themes: Array<{ id: AlertTheme; label: string; colors: string }> = [
      { id: "sunny", label: "Soleil joyeux", colors: "#ffb43b,#ff6d70" },
      { id: "mint", label: "Menthe fraîche", colors: "#31c89b,#087c74" },
      { id: "coral", label: "Corail visible", colors: "#ff7b6f,#db3f74" },
      { id: "blue", label: "Bleu lumineux", colors: "#5a8df5,#7759d8" },
    ];
    return (
      <section className="admin-content-card admin-alert-studio">
        <header>
          <div><span>COMPAGNON WINDOWS · POP-UP LGPI</span><h1>Alerte comptoir</h1><p>Préparez un pop-up visible, joyeux et immédiatement compréhensible par l’équipe.</p></div>
          <b className="admin-alert-ready">● PRÊT À DIFFUSER</b>
        </header>

        <div className="admin-alert-studio-grid">
          <section className="admin-alert-form">
            <div className="admin-alert-form-title"><span>🎨</span><div><h2>Contenu et apparence</h2><p>Le produit et la consigne métier viendront compléter ce modèle au moment du scan.</p></div></div>
            <label><span>Titre du pop-up</span><input value={alertSettings.title} maxLength={120} onChange={(event) => setAlertSettings({ ...alertSettings, title: event.target.value })} /></label>
            <label><span>Message d’introduction</span><textarea value={alertSettings.message} maxLength={500} onChange={(event) => setAlertSettings({ ...alertSettings, message: event.target.value })} /></label>
            <label><span>Texte du bouton de validation</span><input value={alertSettings.buttonLabel} maxLength={48} onChange={(event) => setAlertSettings({ ...alertSettings, buttonLabel: event.target.value })} /></label>

            <fieldset>
              <legend>Ambiance visuelle</legend>
              <div className="admin-alert-themes">{themes.map((theme) => (
                <button key={theme.id} type="button" className={alertSettings.theme === theme.id ? "active" : ""} onClick={() => setAlertSettings({ ...alertSettings, theme: theme.id })}>
                  <i style={{ background: `linear-gradient(135deg, ${theme.colors})` }} />
                  <span>{theme.label}</span>
                  <b>{alertSettings.theme === theme.id ? "✓" : ""}</b>
                </button>
              ))}</div>
            </fieldset>

            <div className="admin-alert-options">
              <article><span>🎉</span><div><strong>Confettis discrets</strong><small>Une touche joyeuse à l’apparition, sans gêner la lecture.</small></div><button type="button" className="admin-switch" aria-pressed={alertSettings.showConfetti} onClick={() => setAlertSettings({ ...alertSettings, showConfetti: !alertSettings.showConfetti })}><i /></button></article>
              <article><span>👁️</span><div><strong>Rester visible</strong><small>Le pop-up reste affiché jusqu’au clic sur « Prise en compte ».</small></div><button type="button" className="admin-switch" aria-pressed={alertSettings.stayVisible} onClick={() => setAlertSettings({ ...alertSettings, stayVisible: !alertSettings.stayVisible })}><i /></button></article>
            </div>

            <section className="admin-counter-scheduler">
              <header><span>⏰</span><div><h2>Programmer une alerte</h2><p>Elle apparaîtra au moment choisi dans les pharmacies et sur les écrans cochés.</p></div></header>
              <label><span>Titre de l’alerte</span><input value={counterAlertDraft.title} maxLength={120} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, title: event.target.value })} placeholder="Ex. Penser à appeler le grossiste" /></label>
              <label><span>Message</span><textarea value={counterAlertDraft.message} maxLength={700} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, message: event.target.value })} placeholder="La consigne courte à afficher à l’équipe" /></label>
              <fieldset><legend>Pharmacie(s)</legend><div className="admin-counter-pharmacies">{adminPharmacies.map((item) => <label key={item.name}><input type="checkbox" checked={counterAlertDraft.pharmacies.includes(item.name)} onChange={() => setCounterAlertDraft({ ...counterAlertDraft, pharmacies: counterAlertDraft.pharmacies.includes(item.name) ? counterAlertDraft.pharmacies.filter((name) => name !== item.name) : [...counterAlertDraft.pharmacies, item.name] })} /> {item.name}</label>)}</div></fieldset>
              <div className="admin-counter-schedule-grid">
                <label><span>Date et heure</span><input type="datetime-local" value={counterAlertDraft.scheduledAt} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, scheduledAt: event.target.value })} /></label>
                <label><span>Répétition</span><select value={counterAlertDraft.recurrence} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, recurrence: event.target.value as CounterAlertRecurrence })}><option value="none">Une seule fois</option><option value="daily">Tous les jours</option><option value="weekdays">En semaine</option><option value="weekly">Chaque semaine</option></select></label>
              </div>
              <fieldset><legend>Afficher le pop-up sur</legend><div className="admin-counter-channels">
                <label><input type="checkbox" checked={counterAlertDraft.channels.windows} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, channels: { ...counterAlertDraft.channels, windows: event.target.checked } })} /> 🖥️ LGPI / compagnon</label>
                <label><input type="checkbox" checked={counterAlertDraft.channels.portal} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, channels: { ...counterAlertDraft.channels, portal: event.target.checked } })} /> 🌐 Portail</label>
                <label><input type="checkbox" checked={counterAlertDraft.channels.tv} onChange={(event) => setCounterAlertDraft({ ...counterAlertDraft, channels: { ...counterAlertDraft.channels, tv: event.target.checked } })} /> 📺 TV</label>
              </div></fieldset>
              <button type="button" className="admin-counter-program" disabled={counterAlertLoading || !counterAlertDraft.title.trim() || !counterAlertDraft.pharmacies.length || !counterAlertDraft.scheduledAt || !Object.values(counterAlertDraft.channels).some(Boolean)} onClick={() => void saveCounterAlert()}>{counterAlertLoading ? "Programmation…" : "＋ Programmer l’alerte"}</button>
              {!!counterAlerts.length && <div className="admin-counter-alert-list">{counterAlerts.slice(0, 8).map((item) => <article key={item.id}><span>{item.status === "active" ? "●" : "○"}</span><div><strong>{item.title}</strong><small>{item.pharmacies.join(", ")} · {new Date(item.scheduledAt).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })} · {item.recurrence === "none" ? "une fois" : item.recurrence === "daily" ? "quotidienne" : item.recurrence === "weekdays" ? "en semaine" : "hebdomadaire"}</small></div><button type="button" onClick={() => void deleteCounterAlert(item)}>×</button></article>)}</div>}
            </section>

            <button type="button" className="admin-alert-save" disabled={alertSettingsLoading || !alertSettings.title.trim() || !alertSettings.buttonLabel.trim()} onClick={() => void saveAlertSettings()}>
              {alertSettingsLoading ? "Enregistrement…" : "💾 Enregistrer et diffuser aux postes"}
            </button>
          </section>

          <aside className="admin-alert-preview-panel">
            <div className="admin-alert-preview-heading"><span>APERÇU SUR LE COMPTOIR</span><b>Au-dessus de LGPI</b></div>
            <div className="admin-alert-desktop">
              <div className="admin-alert-lgpi"><span>LGPI</span><i /><i /><i /></div>
              <div className={`admin-counter-popup theme-${alertSettings.theme}`}>
                {alertSettings.showConfetti && <div className="admin-counter-confetti" aria-hidden="true"><i /><i /><i /><i /><i /><i /></div>}
                <header><span>✦</span><div><small>PORTAIL PHARMA · CONSEIL COMPTOIR</small><strong>{alertSettings.title || "Votre titre apparaîtra ici"}</strong></div></header>
                <p>{alertSettings.message || "Votre message apparaîtra ici."}</p>
                <article><span>💡</span><div><b>Exemple de consigne</b><small>Penser à proposer le produit associé si le besoin du patient correspond.</small></div></article>
                <button type="button" onClick={() => toast("Aperçu : alerte prise en compte")}>{alertSettings.buttonLabel || "✓ Prise en compte"}</button>
                <footer>{alertSettings.stayVisible ? "Reste visible jusqu’à validation" : "Disparaît automatiquement"} · n’interrompt pas la vente</footer>
              </div>
            </div>
            <div className="admin-alert-preview-note"><span>🖥️</span><p>Le compagnon Windows récupérera ce modèle lors de sa prochaine synchronisation. Chaque alerte conserve ensuite son produit et sa consigne propres.</p></div>
          </aside>
        </div>
      </section>
    );
  };

  const renderCompanion = () => {
    const grouped = adminPharmacies.map((pharmacy) => ({
      ...pharmacy,
      workstations: companionStatus.workstations.filter((workstation) => workstation.pharmacy === pharmacy.name),
    }));
    const onlineCount = companionStatus.workstations.filter((workstation) =>
      Date.now() - workstation.last_seen_at < companionStatus.onlineThresholdMs,
    ).length;
    const formatMoment = (timestamp: number) => timestamp
      ? new Intl.DateTimeFormat("fr-FR", { dateStyle: "short", timeStyle: "short" }).format(new Date(timestamp))
      : "Jamais";
    return (
      <section className="admin-content-card admin-companion">
        <header>
          <div><span>POSTES DE VENTE</span><h1>Compagnon Windows</h1><p>Installation autonome, déclencheurs et état des postes, sans intervention quotidienne.</p></div>
          <a className="admin-download-companion" href="/downloads/PharmaScan-Windows.zip">⬇ Télécharger pour Windows</a>
        </header>

        <section className="admin-companion-hero">
          <div className="admin-companion-mark"><img src="/pharmascan-icon.png" alt="Logo PharmaScan" /></div>
          <div>
            <span>PHARMASCAN · VERSION {companionStatus.currentVersion}</span>
            <h2>Le conseil utile apparaît au-dessus de LGPI.</h2>
            <p>Sans installation de .NET : le petit installateur récupère le moteur inclus, puis configure la pharmacie, l’icône Bureau, le démarrage Windows, les mises à jour silencieuses et la désinstallation.</p>
          </div>
          <div className="admin-companion-score"><strong>{onlineCount}</strong><small>poste{onlineCount > 1 ? "s" : ""} en ligne</small></div>
        </section>

        <div className="admin-companion-benefits">
          <article><span>🔄</span><strong>Actualisation toutes les 2 min</strong><small>Chaque scan est aussi vérifié en direct, avec une copie locale en secours.</small></article>
          <article><span>✨</span><strong>Mise à jour autonome</strong><small>La nouvelle version s’installe silencieusement, sans question à l’utilisateur.</small></article>
          <article><span>🏥</span><strong>Une pharmacie par poste</strong><small>Square, Ragadan ou Laurentine est choisie lors de l’installation.</small></article>
          <article><span>🖥️</span><strong>État visible ici</strong><small>Version, présence, déclencheurs chargés et démarrage automatique.</small></article>
          <article><span>🧹</span><strong>Facile à désinstaller</strong><small>Depuis Windows ou directement depuis le menu de PharmaScan.</small></article>
        </div>

        <section className={`admin-nifty-monitor ${companionStatus.niftyState.lastError ? "error" : "healthy"}`}>
          <div className="admin-nifty-monitor-icon">{companionStatus.niftyState.lastError ? "⚠️" : "✓"}</div>
          <div><span>SUPERVISION NIFTY</span><h2>{companionStatus.niftyState.lastError ? "La dernière récupération a échoué" : "Fichier Nifty à jour"}</h2><p>{companionStatus.niftyState.lastError || `${companionStatus.niftyState.eanCount} EAN chargés et distribués aux postes.`}</p></div>
          <dl><div><dt>Dernière mise à jour réussie</dt><dd>{formatMoment(companionStatus.niftyState.lastRefreshAt)}</dd></div><div><dt>Dernière tentative</dt><dd>{formatMoment(companionStatus.niftyState.lastAttemptAt)}</dd></div></dl>
          <button type="button" onClick={() => void refreshNiftyNow()} disabled={companionLoading}>{companionLoading ? "Vérification…" : "↻ Vérifier maintenant"}</button>
        </section>

        <div className="admin-companion-status-title">
          <div><span>ÉTAT EN DIRECT</span><h2>Postes signalés au portail</h2></div>
          <button type="button" onClick={() => void loadCompanionStatus()} disabled={companionLoading}>{companionLoading ? "Actualisation…" : "↻ Actualiser"}</button>
        </div>
        <div className="admin-workstation-groups">
          {grouped.map((group) => (
            <article key={group.name} className={`admin-workstation-group ${group.color}`}>
              <header><span>{group.name === "Square" ? "🏥" : group.name === "Ragadan" ? "🌿" : "✨"}</span><div><h3>{group.name}</h3><p>{group.workstations.length} poste{group.workstations.length > 1 ? "s" : ""} signalé{group.workstations.length > 1 ? "s" : ""}</p></div></header>
              {group.workstations.length === 0 ? <div className="admin-no-workstation">Aucun poste installé ou signalé.</div> : (
                <div className="admin-workstation-list">{group.workstations.map((workstation) => {
                  const online = Date.now() - workstation.last_seen_at < companionStatus.onlineThresholdMs;
                  const upToDate = workstation.version === companionStatus.currentVersion;
                  return (
                    <div key={workstation.workstation_id} className={online ? "online" : "offline"}>
                      <span className="admin-workstation-icon">🖥️</span>
                      <div className="admin-workstation-name"><strong>{workstation.workstation_name}</strong><small>{online ? "● En ligne" : `● Hors ligne · ${formatMoment(workstation.last_seen_at)}`}</small></div>
                      <div><b>{workstation.trigger_count}</b><small>déclencheurs</small></div>
                      <div><b>{workstation.agent_role === "order" ? "Commande" : "Vente"}</b><small>fonction du poste</small></div>
                      <div><b>{workstation.version}</b><small>{upToDate ? "à jour" : "mise à jour prévue"}</small></div>
                      <div><b>{workstation.auto_start ? "Oui" : "Non"}</b><small>démarre avec Windows</small></div>
                      <div><b>{formatMoment(workstation.last_pull_at)}</b><small>dernière synchro</small></div>
                      <div><b>{workstation.last_scan_gtin || "—"}</b><small>{workstation.last_scan_at ? `${workstation.last_scan_alert_count ?? 0} alerte(s) · ${formatMoment(workstation.last_scan_at)}` : "aucun scan reçu"}</small></div>
                    </div>
                  );
                })}</div>
              )}
            </article>
          ))}
        </div>
        <div className="admin-companion-note"><span>ℹ️</span><p>Après installation, le poste apparaît ici dès son premier signal. Le logiciel reste discret près de l’horloge et n’empêche jamais le scan dans LGPI.</p></div>
      </section>
    );
  };

  const renderSettings = () => (
    <section className="admin-content-card admin-settings-page">
      <header><div><span>PRÉFÉRENCES GROUPE</span><h1>Réglages</h1><p>Utilisateur du portail, intelligence artificielle et alertes équipe.</p></div></header>

      <section className="admin-portal-user">
        <div className="admin-setting-icon">👤</div>
        <div>
          <span>UTILISATEUR ACTIF DU PORTAIL</span>
          <h2>Qui utilise ce poste&nbsp;?</h2>
          <p>Ce choix alimente les attributions, les blocs-notes et les signatures. Il n’apparaît plus sur la page d’accueil.</p>
        </div>
        <label>
          <span>Utilisateur</span>
          <select value={portalOperator} onChange={(event) => setPortalOperator(event.target.value)}>
            {operators.map((operator) => (
              <option key={operator.name} value={operator.name}>{operator.name} · {operator.pharmacy} · {operator.role}</option>
            ))}
          </select>
        </label>
        <button type="button" onClick={() => {
          window.localStorage.setItem("portal-active-operator", portalOperator);
          toast(`${portalOperator} est désormais l’utilisateur actif de ce poste`);
        }}>✓ Utiliser ce profil</button>
      </section>

      <section className="admin-backup-setting">
        <div className="admin-setting-icon">🛟</div>
        <div className="admin-backup-copy">
          <span>SAUVEGARDE COMPLÈTE · INSTANT T</span>
          <h2>Exporter ou restaurer les données du portail</h2>
          <p>Inclut notamment fiches et suivis laboratoire, passations, tâches, bloc-notes, patients, commandes, PERFADOM, catalogues, réglages et index du Drive.</p>
        </div>
        <button type="button" className="primary" disabled={backupBusy} onClick={() => void exportPortalBackup()}>{backupBusy ? "Traitement…" : "⬇ Exporter maintenant"}</button>
        <label className="admin-backup-file">
          <span>Réimporter une sauvegarde JSON</span>
          <input type="file" accept="application/json,.json" disabled={backupBusy} onChange={(event) => void inspectBackupFile(event.target.files?.[0] ?? null)} />
        </label>
        {backupPreview && <div className="admin-backup-preview"><strong>✓ Sauvegarde reconnue</strong><span>{backupPreview.tables} rubriques · {backupPreview.rows.toLocaleString("fr-FR")} données · {backupPreview.exportedAt ? new Date(backupPreview.exportedAt).toLocaleString("fr-FR") : "date inconnue"}</span><button type="button" disabled={backupBusy} onClick={() => void restorePortalBackup()}>↥ Restaurer cette sauvegarde</button></div>}
        <small>La restauration fusionne la sauvegarde avec la base actuelle : elle restaure les mêmes identifiants sans effacer les nouveaux éléments créés depuis.</small>
      </section>

      <section className={`admin-ai-setting ${aiStatus?.configured ? "configured" : ""}`}>
        <div className="admin-setting-icon">✦</div>
        <div>
          <span>ASSISTANT OPENAI · DROIT DE PRESCRIPTION</span>
          <h2>{aiStatus?.configured ? "Assistant connecté" : "Clé serveur à configurer"}</h2>
          <p>La clé reste dans les secrets du serveur. Elle n’est jamais affichée, stockée dans le navigateur ou envoyée avec la page.</p>
        </div>
        <strong>{aiStatus?.configured ? "● PRÊT" : "○ À CONFIGURER"}</strong>
        <small>Variable attendue&nbsp;: OPENAI_API_KEY{aiStatus?.model ? ` · modèle ${aiStatus.model}` : ""}</small>
      </section>

      <div className="admin-settings-list">{[
        ["tvAlerts", "📺", "Alertes sur la TV", "Afficher automatiquement les informations arrivées à échéance."],
        ["overdueAlerts", "⏰", "Alerte en cas de retard", "Mettre en avant les missions et livraisons non traitées."],
        ["patientNamesOnTv", "👤", "Nom complet des patients", "Afficher le nom complet pour les rappels patients sur l’écran équipe."],
        ["dailyDigest", "📊", "Résumé de fin de journée", "Préparer une synthèse quotidienne pour les titulaires."],
      ].map(([key, icon, title, description]) => (
        <article key={key}>
          <span>{icon}</span><div><strong>{title}</strong><small>{description}</small></div>
          <button type="button" className="admin-switch" aria-pressed={settings[key as keyof typeof settings]} onClick={() => setSettings((current) => ({ ...current, [key]: !current[key as keyof typeof current] }))}><i /></button>
        </article>
      ))}</div>
      <div className="admin-save-row"><p>Ces réglages seront ensuite reliés aux droits par pharmacie et par fonction.</p><button type="button" onClick={() => toast("Préférences enregistrées pour cette session")}>💾 Enregistrer</button></div>
    </section>
  );

  return (
    <main className="admin-app">
      <aside className="admin-sidebar">
        <a className="admin-brand" href="/"><span>SL</span><div><strong>Portail Pharma</strong><small>Administration</small></div></a>
        <nav>
          <small>GESTION OFFICINE</small>
          {([
            ["Vue d’ensemble", "📊"],
            ["Pharmacies", "🏥"],
            ["Opérateurs", "👥"],
            ["Challenges", "🏁"],
          ] as Array<[AdminSection, string]>).map(([item, icon]) => <button type="button" key={item} className={section === item ? "active" : ""} onClick={() => setSection(item)}><span>{icon}</span>{item}</button>)}
          <small>PARAMÉTRAGES</small>
          {([
            ["Modules", "🧩"],
            ["Drive", "🗂️"],
            ["Catalogues LGPI", "🏷️"],
            ["Alerte comptoir", "✨"],
            ["Compagnon Windows", "🖥️"],
            ["Réglages", "⚙️"],
          ] as Array<[AdminSection, string]>).map(([item, icon]) => <button type="button" key={item} className={section === item ? "active" : ""} onClick={() => setSection(item)}><span>{icon}</span>{item === "Alerte comptoir" ? "Fin de vente LGPI" : item}</button>)}
        </nav>
        <div className="admin-sidebar-card"><span>🔐</span><strong>Espace titulaires</strong><p>Configuration du réseau et des équipes.</p></div>
        <a className="admin-back" href="/">← Retour au portail</a>
      </aside>
      <section className="admin-main">
        <header className="admin-topbar"><div><span className="admin-live"><i /> Réseau synchronisé</span><strong>Administration</strong></div><div><button type="button" onClick={() => toast("Aucune alerte critique")}>🔔</button><span className="admin-avatar">VB</span></div></header>
        <div className="admin-content">{section === "Vue d’ensemble" ? renderOverview() : section === "Pharmacies" ? renderPharmacies() : section === "Opérateurs" ? renderOperators() : section === "Challenges" ? <AdminChallenges /> : section === "Modules" ? renderModules() : section === "Drive" ? <DriveAdminPanel /> : section === "Catalogues LGPI" ? <AdminCatalogImport /> : section === "Alerte comptoir" ? renderAlertStudio() : section === "Compagnon Windows" ? renderCompanion() : renderSettings()}</div>
      </section>
      {notice && <div className="admin-toast">✅ {notice}</div>}
    </main>
  );
}
